/**
 * Which brands belong in the sidebar.
 *
 * A brand row is created the moment the app is installed, with `approved: false`, and stays
 * there until an admin approves its access request. Until then it has no orders, no billing
 * and nothing to configure — every tab on its page would be empty — yet it sat in the sidebar
 * beside brands that are live, indistinguishable from them.
 *
 * A rule rather than an inline filter because it is a rule: the sidebar is the only route to
 * a brand page (`/brands` redirects to the dashboard), so what this returns decides what an
 * admin can reach. Pending brands are handled on Access Requests, which is where the decision
 * about them is actually made.
 *
 * NOT applied to the request that fetches brands. useBrands is shared with the Dashboard,
 * Analytics, Invoices, Order Management and the adjustments table, and narrowing the query
 * would silently change all of them — they cache under one React Query key, `['brands', {}]`,
 * so a different result for one caller is the result for every caller.
 */

/**
 * @param {unknown} brands whatever the brands query returned, including nothing
 * @returns {Array<{id: string, name: string}>} approved brands, safe to map over
 */
export function approvedBrandsOnly(brands) {
  if (!Array.isArray(brands)) return [];

  return brands.filter((brand) => (
    // Every field the sidebar dereferences, checked before it does. A row missing an id would
    // render a link to /brands/undefined.
    brand
    && typeof brand === 'object'
    && brand.id
    && brand.name
    // Strictly true, not merely truthy. `approved` is a non-nullable boolean on the brand, so
    // anything else here means the payload was not what this function was written against —
    // and an unrecognised value should hide a brand rather than show it. Hiding an approved
    // brand is a visible annoyance; showing an unapproved one is the bug being fixed.
    && brand.approved === true
  ));
}
